/**
 * IapSuccessDialog — animated purchase success confirmation.
 * Shows pack name, old balance → credits added → new balance (bouncing + glowing).
 */

import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import type { IapSuccessInfo } from '@/store/accountStore.ts';
import styles from './IapSuccessDialog.module.css';

interface Props {
  info: IapSuccessInfo;
  onDismiss: () => void;
}

export function IapSuccessDialog({ info, onDismiss }: Props) {
  const { t } = useTranslation('account');
  const [phase, setPhase] = useState(0);

  useEffect(() => {
    const timers = [
      setTimeout(() => setPhase(1), 400),   // old balance
      setTimeout(() => setPhase(2), 1000),   // +credits
      setTimeout(() => setPhase(3), 1600),   // new balance bounce
      setTimeout(() => setPhase(4), 2200),   // glow + continue button
    ];
    return () => timers.forEach(clearTimeout);
  }, []);

  return (
    <div className={styles.backdrop}>
      <div className={styles.dialog} onClick={e => e.stopPropagation()}>
        <div className={styles.title}>{t('iapSuccessTitle')}</div>
        <div className={styles.packName}>
          {t('iapSuccessPackMessage', { pack: info.packLabel })}
        </div>

        <div className={styles.balanceSequence}>
          {/* Old balance */}
          <div className={`${styles.balanceRow} ${phase >= 1 ? styles.visible : ''}`}>
            <div className={styles.oldBalanceLabel}>{t('iapSuccessPreviousBalance')}</div>
            <div className={styles.oldBalanceValue}>
              {info.oldBalance.toLocaleString()} {t('creditsLabel')}
            </div>
          </div>

          {/* Credits added */}
          <div className={`${styles.balanceRow} ${phase >= 2 ? styles.visible : ''}`}>
            <div className={styles.creditsAdded}>
              +{info.creditsAdded.toLocaleString()} {t('creditsLabel')}
            </div>
          </div>

          {/* New balance */}
          <div className={`${styles.balanceRow} ${phase >= 3 ? styles.visible : ''}`}>
            <div className={styles.newBalanceLabel}>{t('iapSuccessNewBalance')}</div>
            <div className={`${styles.newBalanceValue} ${phase >= 3 ? styles.glowing : ''}`}>
              {info.newBalance.toLocaleString()}
              <span className={styles.creditsUnit}> {t('creditsLabel')}</span>
            </div>
          </div>
        </div>

        <button
          className={`${styles.continueBtn} ${phase >= 4 ? styles.visible : ''}`}
          onClick={onDismiss}
        >
          {t('continueButton')}
        </button>
      </div>
    </div>
  );
}
